import { publishQueries, publishUserQueries } from '@/entities/sets/publish';
import { usePersistStore } from '@/shared/lib/store';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { ErrorPopup } from '@/shared/ui';
import { outputArr, sizeArr, templateArr } from '@/shared/utils/pod';
import { showToast } from '@/shared/utils/toast';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PodHeader } from '@/widgets/section-header';
import { useQueryClient } from '@tanstack/react-query';
import { publishedBoxApi } from '@/shared/api/sets/publish';
import { decryptDataDeterministic } from '@/shared/utils/publish';
import { z } from 'zod';

const podSearchSchema = z.object({
    podData: z.string().optional(),
    publishId: z.number().optional(),
    publishGrpIdParam: z.number().optional(),
});

// LEGACY(POD1): 활성 호환 라우트입니다. 신규 진입점은 /sets/pod-v2를 사용합니다.
export const Route = createFileRoute('/sets/pod/')({
    validateSearch: (search) => podSearchSchema.parse(search),
    component: RouteComponent,
});

const CDN_URL = import.meta.env.VITE_TTUK_POD_CDN;

function RouteComponent() {
    const queryClient = useQueryClient();
    const { showModal, closeModal } = useModalStore();
    const { sUserId, sDefaultUserId } = usePersistStore();
    const router = useRouter();
    const { podData: dataParam, publishId: publishIdParam, publishGrpIdParam } = Route.useSearch();

    const [data, setData] = useState<{
        examIdList: string[];
        curriBook: string;
        subject: string;
    }>(); // pod_answer 정답 및 해설 Y/N
    const [outputOption, setOutputOption] = useState<string[]>(['pod_answer', 'pod_description']); // pod_answer 정답 및 해설 Y/N
    const [sizeOption, setSizeOption] = useState<string>('pod_letter_A4'); // pod_letter 용지 사이즈 A4, A3, B4
    const [viewOption, setViewOption] = useState<string>('pod_section_1'); // pod_section 문제 간격 설정 pod_section_1 1단, pod_section_2_(narrow, wide, morewide) 2단
    const [templateOption, setTemplateOption] = useState<string>('pod_template_01');

    const timestamp = Date.now();
    const registerStudent = publishUserQueries.postRegisterUser();
    const savePublish = publishQueries.publish.postPublishExam();

    const { data: publishData } = publishQueries.detail.getPublishDetail(
        {
            publishId: publishIdParam ? Number(publishIdParam) : -0,
            wrterId: sUserId,
        },
        !!publishIdParam && !!sUserId
    );

    const backUrl = () => {
        return publishGrpIdParam ? `/sets/created-box` : '/sets/published-box';
    };

    const claId =
        'viva-' +
        sDefaultUserId +
        '-' +
        timestamp +
        '-' +
        (dataParam ? data?.curriBook : publishData?.publishInfoDetail?.curriBook);

    useEffect(() => {
        return () => {
            queryClient.resetQueries({ queryKey: ['publish-detail'] });
        };
    }, []);

    useEffect(() => {
        if (publishData && publishData?.publishInfoDetail?.podOptionList) {
            const legacy = publishData?.publishInfoDetail.podOptionList;
            publishData?.publishInfoDetail.podOptionList.map((e) => {
                return;
            });
            const newCheckList = [];
            if (legacy.find((item) => item.code === 'pod_answer')?.val === 'Y') {
                newCheckList.push('pod_answer');
            }
            if (legacy.find((item) => item.code === 'pod_description')?.val === 'Y') {
                newCheckList.push('pod_description');
            }
            if (legacy.find((item) => item.code === 'pod_eng_description')?.val === 'Y') {
                newCheckList.push('pod_eng_description');
            }
            setOutputOption(newCheckList);
            setSizeOption(
                legacy.find((item) => item.code === 'pod_letter')?.val ?? 'pod_letter_A4'
            );
            setViewOption(
                legacy.find((item) => item.code === 'pod_section')?.val ?? 'pod_section_1'
            );
            setTemplateOption(
                legacy.find((item) => item.code === 'pod_template')?.val ?? 'pod_template_01'
            );
        }
    }, [publishData]);

    useEffect(() => {
        fnDecryptedData();
    }, [dataParam]);

    const fnDecryptedData = async () => {
        if (dataParam) {
            try {
                const decryptedData = decryptDataDeterministic(dataParam);

                setData({
                    examIdList: decryptedData.examIdList,
                    curriBook: decryptedData.curriBook,
                    subject: decryptedData.subject,
                });
                decryptedData.subject === '영어' &&
                    setOutputOption(['pod_answer', 'pod_description', 'pod_eng_description']);
            } catch (error) {
                console.error('복호화 실패:', error);
            }
        }
    };

    const handleCheck = (value: string) => {
        setOutputOption((prev) => {
            if (prev.includes(value)) {
                return prev.filter((item) => item !== value);
            } else {
                return [...prev, value];
            }
        });
    };

    const handleClaIdVerification = async (claId: string) => {
        const res = await publishedBoxApi.getClaIdVerification({ claId: claId });
        if (!res?.claInfo) return true;
        else return false;
    };

    const onRegister = async (type?: 'preview' | 'leave') => {
        if (!(await handleClaIdVerification(claId))) {
            return showToast('클래스 ID 인증 실패 다시 시도해주세요.');
        }
        registerStudent.mutate(
            {
                tcUserId: sUserId,
                claList: [
                    {
                        claId: claId,
                        schlCd: '',
                        schlNm: '',
                        year: '',
                        claNm: '',
                        gradeCd: '',
                        stdtUserInfo: [],
                    },
                ],
            },
            {
                onSuccess: (res) => {
                    if (res?.resultOk) {
                        onPublish(type);
                    } else alert('회원 정보 등록 실패');
                },
                onError: () =>
                    showModal({
                        isCustom: true,
                        id: 'error',
                        initContents: () => <ErrorPopup />,
                    }),
            }
        );
    };

    const onPublish = (type?: 'preview' | 'leave') => {
        savePublish.mutate(
            {
                examIdList: dataParam
                    ? data?.examIdList!
                    : [publishData?.publishInfoDetail?.examId?.toString()!],
                tcUserId: sUserId,
                publishType: 3,
                claList: [
                    {
                        claId: claId,
                        stdtUserId: [],
                    },
                ],
                podOptionList: [
                    {
                        code: 'pod_answer',
                        val: outputOption.includes('pod_answer') ? 'Y' : 'N',
                    },
                    {
                        code: 'pod_description',
                        val: outputOption.includes('pod_description') ? 'Y' : 'N',
                    },
                    {
                        code: 'pod_eng_description',
                        val: outputOption.includes('pod_eng_description') ? 'Y' : 'N',
                    },
                    {
                        code: 'pod_letter',
                        val: sizeOption,
                    },
                    {
                        code: 'pod_section',
                        val: viewOption,
                    },
                    {
                        code: 'pod_template',
                        val: templateOption,
                    },
                    {
                        code: 'pod_qr',
                        val: 'pod_qr_last',
                    },
                ],
            },
            {
                onSuccess: (res) => {
                    if (res?.resultOk) {
                        type === 'leave'
                            ? router.navigate({ to: '/sets/created-box' })
                            : onPreview(res.publishGrpId);

                        closeModal('pod-exit-popup');
                    } else alert('발행 실패');
                },
                onError: () =>
                    showModal({
                        isCustom: true,
                        id: 'error',
                        initContents: () => <ErrorPopup />,
                    }),
            }
        );
    };

    const onPreview = (publishGrpId: number) => {
        router.navigate({
            to: '/sets/pod/preview/' + publishGrpId,
            search: dataParam ? { podData: dataParam } : undefined,
        });
    };

    return (
        <>
            <PodHeader
                onRegister={onRegister}
                backUrl={backUrl()}
                exitUrl={
                    publishIdParam ? `/sets/published-box/detail/${publishIdParam}` : undefined
                }
            />
            {/* [뚝딱] gnb 하위 탭 메뉴 없는 컨텐츠 type3_conts 클래스 추가 */}
            <section className="contwrap print_wrap">
                <div className="contInner" id="printOption">
                    <div className="pop_wrap publish_option pod_pop_wrap">
                        <div className="pop_contents publish_option_popcont">
                            <div className="flex_c pod_conts">
                                <div className="pod_pop_data pod_pop_left">
                                    <div className="set-file-box step1">
                                        <div className="set-file-header">
                                            <div className="set-file-title">
                                                <span className="title step_tab_title">
                                                    STEP.01
                                                </span>
                                                <h3 className="title step_tab_title2">
                                                    시험지 옵션 선택
                                                </h3>
                                            </div>
                                        </div>
                                        <div className="set-file-content content-type1">
                                            <div className="form-row block">
                                                <div className="form-label title_wrap">
                                                    <div className="option_title">
                                                        정답 및 해설 출력 선택
                                                        <span className="icon_mark notice4 hover">
                                                            <i>
                                                                <span className="textCloud type3">
                                                                    선택한 값이 마지막 문항 다음
                                                                    페이지에 표시됩니다.
                                                                </span>
                                                            </i>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="form-td">
                                                    <div className="cont_wrap">
                                                        <div className="cont_wrap_inner">
                                                            <div className="step_info_wrap">
                                                                <div className="chkList">
                                                                    <div className="chkbox chk_qna_setting">
                                                                        {outputArr.map(
                                                                            (item, idx) => {
                                                                                if (
                                                                                    idx === 2 &&
                                                                                    ((data?.subject &&
                                                                                        data?.subject !==
                                                                                            '영어') ||
                                                                                        publishData
                                                                                            ?.publishInfoDetail
                                                                                            ?.curriSubjectVal !==
                                                                                            '영어')
                                                                                )
                                                                                    return;
                                                                                return (
                                                                                    <span
                                                                                        key={`output-pod-${idx + 1}`}
                                                                                        className="chk"
                                                                                    >
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            id={`chk${idx + 1}`}
                                                                                            name={`chk${idx + 1}`}
                                                                                            value={`chk${idx + 1}`}
                                                                                            checked={outputOption.includes(
                                                                                                item.value
                                                                                            )}
                                                                                            onChange={() =>
                                                                                                handleCheck(
                                                                                                    item.value
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                        <label
                                                                                            htmlFor={`chk${idx + 1}`}
                                                                                        >
                                                                                            {
                                                                                                item.label
                                                                                            }
                                                                                        </label>
                                                                                    </span>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-row block">
                                                <div className="form-label title_wrap">
                                                    <div className="option_title">용지 사이즈</div>
                                                </div>
                                                <div className="form-td">
                                                    <div className="cont_wrap">
                                                        <div className="cont_wrap_inner cont-mt print_radioBtn size_radiobtn">
                                                            <div className="step_info_wrap">
                                                                <div className="flex_b radiobtn_wrap">
                                                                    {sizeArr.map((item, idx) => (
                                                                        <div
                                                                            key={`size-pod-${idx + 1}`}
                                                                            className="chk-group chk-radioBtn2 type3"
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                id={`rdo${idx + 1}`}
                                                                                name={`rdo${idx + 1}`}
                                                                                value={`rdo${idx + 1}`}
                                                                                checked={
                                                                                    sizeOption ===
                                                                                    item.value
                                                                                }
                                                                                onChange={() =>
                                                                                    setSizeOption(
                                                                                        item.value
                                                                                    )
                                                                                }
                                                                            />
                                                                            <label
                                                                                className="input-chk"
                                                                                htmlFor={`rdo${idx + 1}`}
                                                                            >
                                                                                <span className="chk-txt">
                                                                                    {item.label}
                                                                                    <i></i>
                                                                                </span>
                                                                            </label>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {/* 주석 처리된 부분은 생략 */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* <div className="form-row block">
                                                <div className="form-label title_wrap">
                                                    <div className="option_title">
                                                        단 선택 및 문제별 간격
                                                    </div>
                                                </div>
                                                <div className="form-td block">
                                                    <div className="cont_wrap">
                                                        <div className="cont_wrap_inner cont-mt part_radiobtn">
                                                            <div className="flex_s radiobtn_wrap">
                                                                {viewOptionArr.map((item, idx) => (
                                                                    <div
                                                                        key={`view-pod-${idx + 10}`}
                                                                        className="chk-group chk-radioBtn2"
                                                                    >
                                                                        <input
                                                                            disabled={item.disabled}
                                                                            type="radio"
                                                                            id={`rdo${idx + 10}`}
                                                                            name={`rdo${idx + 10}`}
                                                                            value={`rdo${idx + 10}`}
                                                                            checked={
                                                                                viewOption ===
                                                                                item.value
                                                                            }
                                                                            onChange={() =>
                                                                                setViewOption(
                                                                                    item.value
                                                                                )
                                                                            }
                                                                        />
                                                                        {item.disabled ? (
                                                                            <ToolTip
                                                                                width={'100%'}
                                                                                msg={
                                                                                    <p className="tip-txt">
                                                                                        Coming Soon
                                                                                    </p>
                                                                                }
                                                                                pos="top"
                                                                            >
                                                                                <label
                                                                                    className="input-chk"
                                                                                    htmlFor={`rdo${idx + 10}`}
                                                                                >
                                                                                    <span className="chk-txt">
                                                                                        {item.label}
                                                                                        <i></i>
                                                                                    </span>
                                                                                </label>
                                                                            </ToolTip>
                                                                        ) : (
                                                                            <label
                                                                                className="input-chk"
                                                                                htmlFor={`rdo${idx + 10}`}
                                                                            >
                                                                                <span className="chk-txt">
                                                                                    {item.label}
                                                                                    <i></i>
                                                                                </span>
                                                                            </label>
                                                                        )}
                                                                    </div>
                                                                ))} */}
                                            {/* 25.05.21 퍼블 추가 */}
                                            {/* <div className="borderBox radiobtn_notice">
                                                                    <p>
                                                                        [2단]으로 선택 시,<br />
                                                                        일부 문항의 레이아웃이<br />
                                                                        PC에서 확인한 것과<br />
                                                                        다르게 보일 수 있습니다.
                                                                    </p>
                                                                </div> */}
                                            {/* </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                                <div className="pod_pop_data pod_pop_right">
                                    <div className="set-file-box step2">
                                        <div className="set-file-header">
                                            <div className="set-file-title">
                                                <span className="title step_tab_title">
                                                    STEP.02
                                                </span>
                                                <h3 className="title step_tab_title2">
                                                    상단 디자인 선택
                                                </h3>
                                            </div>
                                        </div>
                                        <div className="template_chocieWrap">
                                            <div className="set-file-content content-type1 template_chocie">
                                                <div className="form-row block">
                                                    <div className="form-label size-type2 title_wrap blind">
                                                        <div className="option_title">
                                                            분할 선택
                                                        </div>
                                                    </div>
                                                    <div className="form-td">
                                                        <div className="cont_wrap">
                                                            <div className="cont_wrap_inner template_radiobtn">
                                                                <div className="radiobtn_wrap">
                                                                    {templateArr.map(
                                                                        (item, idx) => (
                                                                            <div
                                                                                key={`template-pod-${idx + 20}`}
                                                                                className="chk-group chk-radioBtn2"
                                                                            >
                                                                                <input
                                                                                    type="radio"
                                                                                    id={`temp${idx + 20}`}
                                                                                    name={`temp${idx + 20}`}
                                                                                    checked={
                                                                                        templateOption ===
                                                                                        item.value
                                                                                    }
                                                                                    onChange={() =>
                                                                                        setTemplateOption(
                                                                                            item.value
                                                                                        )
                                                                                    }
                                                                                />
                                                                                <label
                                                                                    className="input-chk"
                                                                                    htmlFor={`temp${idx + 20}`}
                                                                                >
                                                                                    <i></i>
                                                                                    <span
                                                                                        className={`template_box temp_${idx + 20}`}
                                                                                    >
                                                                                        <img
                                                                                            src={
                                                                                                CDN_URL +
                                                                                                item.img
                                                                                            }
                                                                                            alt={
                                                                                                item.label
                                                                                            }
                                                                                        />
                                                                                    </span>
                                                                                </label>
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
